import React from "react";
import { useParams } from "react-router-dom";
import { BsStar, BsStarFill } from "react-icons/bs";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getCustomer, toggleCustomerFavorite } from "../customersSlice";
import { getCustomerActiveOrders, getCustomerPendingOrders, getCustomerCompletedOrders } from "../customerOrdersSlice";
import { AdminActiveOrdersTable } from "../components/AdminActiveOrdersTable/AdminActiveOrdersTable";
import { AdminPendingOrdersTable } from "../components/AdminPendingOrdersTable/AdminPendingOrdersTable";
import { AdminOrderHistoryTable } from "../components/AdminOrderHistoryTable/AdminOrderHistoryTable";
import "./CustomerDetail.scss";

const PAGE = 20;

type Tab = "current" | "pending" | "completed";
const TABS: { id: Tab; label: string }[] = [
  { id: "current", label: "Current" },
  { id: "pending", label: "Pending" },
  { id: "completed", label: "Completed" },
];

export function CustomerDetail() {
  const { id = "" } = useParams();
  const dispatch = useAppDispatch();
  const customer = useAppSelector((s) => s.customers.selectedCustomer);
  const getResult = useAppSelector((s) => s.customers.getCustomerResult);
  const activeOrders = useAppSelector((s) => s.customerOrders.activeOrders);
  const pendingOrders = useAppSelector((s) => s.customerOrders.pendingOrders);
  const completedOrders = useAppSelector((s) => s.customerOrders.completedOrders);

  const [activeTab, setActiveTab] = React.useState<Tab>("current");
  const [offset, setOffset] = React.useState(0);

  // Customer header.
  React.useEffect(() => {
    if (id) dispatch(getCustomer(id));
  }, [dispatch, id]);

  // Fetch the active tab's page (20 per page). Re-runs on tab or page change.
  React.useEffect(() => {
    if (!id) return;
    if (activeTab === "current") dispatch(getCustomerActiveOrders({ userId: id, offset, count: PAGE }));
    else if (activeTab === "pending") dispatch(getCustomerPendingOrders({ userId: id, offset, count: PAGE }));
    else dispatch(getCustomerCompletedOrders({ userId: id, offset, count: PAGE }));
  }, [dispatch, id, activeTab, offset]);

  const changeTab = (t: Tab) => { setActiveTab(t); setOffset(0); };

  const rowsForTab =
    activeTab === "current" ? activeOrders : activeTab === "pending" ? pendingOrders : completedOrders;

  const Fav = customer?.isFavorite ? BsStarFill : BsStar;

  return (
    <div className="customer-detail-component">
      {customer && (
        <div className="header">
          <div className="title-section">
            <h1>{customer.firstName} {customer.lastName}</h1>
            <div className="subtitle">
              {customer.email} · {customer.phoneNumber} · Open {customer.openOrdersCount} · Past {customer.pastOrdersCount}
            </div>
          </div>
          <Fav
            data-testid="detail-favorite"
            className={`favorite-icon ${customer.isFavorite ? "on" : ""}`}
            onClick={() => dispatch(toggleCustomerFavorite({ id: customer.id, isFavorite: !customer.isFavorite }))}
          />
        </div>
      )}

      {getResult.loading && !customer && <div className="state-msg">Loading…</div>}
      {getResult.error && !getResult.loading && <div className="state-msg">Customer not found.</div>}

      {!getResult.error && (
        <>
          <div className="nav-tabs">
            {TABS.map((t) => (
              <div
                key={t.id}
                className={`nav-tab ${activeTab === t.id ? "active" : ""}`}
                onClick={() => changeTab(t.id)}
              >
                {t.label}
              </div>
            ))}
          </div>

          <div className="container">
            {rowsForTab.length === 0 ? (
              <div className="empty-orders">No {activeTab === "completed" ? "past" : activeTab} orders.</div>
            ) : (
              <div className="table-card">
                {activeTab === "current" && <AdminActiveOrdersTable />}
                {activeTab === "pending" && <AdminPendingOrdersTable />}
                {activeTab === "completed" && <AdminOrderHistoryTable />}
              </div>
            )}
            {(rowsForTab.length > 0 || offset > 0) && (
              <div className="pagination">
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</button>
                <button disabled={rowsForTab.length < PAGE} onClick={() => setOffset(offset + PAGE)}>Next</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
